import { CheckoutResponse, PaymentResponse, PlanResponse, SimpleStringResponse } from '../../dto/ResDtoRepository';
import { useApiWrapper } from './apiWrapper';
import { createCheckout, getProduct, getCurrentPlan, getCustomerPortalUrl, requestAccess, submitPoll } from '../../api/paymentApi';
import { ProductData, PlanData, CustomerPortalUrlData } from '../../dto/EntityRepository';
import { usePaymentStore } from '../../store/paymentStore';
import { getConfig } from '../../env/envManager';
import { openNewTab, redirectToPolar } from '../../util/openNewTab';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { TempAlertType, TempAlertPosition, AlertPosition, AlertType } from '../../enum/EnumRepository';
import { useModalStore } from '../../store/portal/modalStore';
import { CreatePollReq } from '../../dto/ReqDtoRepository';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { getAlertMessage } from '../../constants/alertMessages';
import { getTempAlertMessage } from '../../constants/tempAlertMessages';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';

interface UsePaymentResult {
  product: ProductData | null;
  getProduct: () => Promise<void>;
  createCheckout: () => Promise<void>;
  currentPlan: PlanData | null;
  getCurrentPlan: () => Promise<void>;
  customerPortalUrl: CustomerPortalUrlData | null;
  getCustomerPortalUrl: () => Promise<void>;
  requestAccess: () => Promise<void>;
  submitPoll: (data: CreatePollReq) => Promise<void>;
  isPortalFresh: () => boolean;
}

export const usePayment = (): UsePaymentResult => {
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const withApiHandler = useApiWrapper();
  const { clearModal } = useModalStore();
  const { product, setProduct, currentPlan, setCurrentPlan, customerPortalUrl, setCustomerPortalUrl, isPortalFresh } = usePaymentStore();
  const { polarBaseUrl } = getConfig();
  const { setTempAlert } = useTempAlertStore();
  const { enqueueAlert } = useStandardAlertStore();
  

  const handleProductSuccess = (response: PaymentResponse) => {
    const { data } = response;
    setProduct(data);
  };

  const handleCurrentPlanSuccess = (response: PlanResponse) => {
    const { data } = response;
    setCurrentPlan(data);
  };

  const handleCreateCheckoutSuccess = (response: CheckoutResponse) => {
    const { data } = response;
    const redirectUrl = data.url;

    if (!redirectUrl.startsWith(polarBaseUrl)) {
      throw new Error(`Unexpected URL: ${redirectUrl}`);
    }

    setTempAlert({
      type: TempAlertType.PAYMENT_PROCESSING,
      position: TempAlertPosition.CC,
      content: getTempAlertMessage('REDIRECTING_POLAR_SUBSCRIPTION'),
      duration: 2000,
      callBack: () => {
        redirectToPolar(redirectUrl);
      }
    });
  };

  const handleCustomerPortalUrlSuccess = (response: SimpleStringResponse) => {
    const { data } = response;
    const redirectUrl = data.value;

    if (!redirectUrl || !redirectUrl.startsWith(polarBaseUrl)) {
      throw new Error(`Unexpected URL: ${redirectUrl}`);
    }
    setCustomerPortalUrl(redirectUrl);
    openNewTab(redirectUrl);
  };

  const handleRequestAccessSuccess = () => {
    setTempAlert({
      type: TempAlertType.UPDATED,
      position: TempAlertPosition.RB,
      content: getTempAlertMessage('THANK_YOU_INTEREST'),
      duration: 2000
    });
    clearModal();
  };
  
  const handleSubmitPollSuccess = (handle: string) => {
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('THANK_YOU_PARTICIPATION'),
      callBack: () => {
        window.location.href = `/@${handle}`;
      }
    });
  };

  const handlePaymentRequest = async (
    action: 'get_product' | 'create_checkout' | 'get_current_plan' | 'get_customer_url' | 'request_access' | 'submit_poll',
    data?: CreatePollReq
  ) => {
    if (!pageOwner) return;
    const { aui, handle } = pageOwner;
    const apiFunction = async () => {
      switch (action) {
        case 'submit_poll':
          return runCurrentArchiveRequest(
            aui,
            () => submitPoll(aui, data as CreatePollReq),
            () => handleSubmitPollSuccess(handle),
          );
        case 'get_product':
          return runCurrentArchiveRequest(
            aui,
            () => getProduct(aui),
            handleProductSuccess,
          );
        case 'create_checkout':
          return runCurrentArchiveRequest(
            aui,
            () => createCheckout(aui),
            handleCreateCheckoutSuccess,
          );
        case 'get_current_plan':
          return runCurrentArchiveRequest(
            aui,
            () => getCurrentPlan(aui),
            handleCurrentPlanSuccess,
          );
        case 'get_customer_url':
          return runCurrentArchiveRequest(
            aui,
            () => getCustomerPortalUrl(aui),
            handleCustomerPortalUrlSuccess,
          );
        case 'request_access':
          return runCurrentArchiveRequest(
            aui,
            () => requestAccess(aui),
            handleRequestAccessSuccess,
          );
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  const getProductHandler = () => handlePaymentRequest('get_product');
  const createCheckoutHandler = () => handlePaymentRequest('create_checkout');
  const getCurrentPlanHandler = () => handlePaymentRequest('get_current_plan');
  const getCustomerPortalUrlHandler = () => handlePaymentRequest('get_customer_url');
  const requestAccessHandler = () => handlePaymentRequest('request_access');
  const submitPollHandler = (data: CreatePollReq) => handlePaymentRequest('submit_poll', data);

  return {
    product,
    getProduct: getProductHandler,
    createCheckout: createCheckoutHandler,
    currentPlan,
    getCurrentPlan: getCurrentPlanHandler,
    customerPortalUrl,
    getCustomerPortalUrl: getCustomerPortalUrlHandler,
    requestAccess: requestAccessHandler,
    submitPoll: submitPollHandler,
    isPortalFresh
  };
};
